// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the phone page header in the WhatsApp thread and mail draft look (JP 2026-11-02): a bar
// the colour of the page, cut from the content by a hairline; a glass back (or close) pill; an
// optional photo or icon with the title over a subtitle; the actions in one glass capsule, and
// ink for the one thing to do (Send, +). Top-level pages keep their big titles and use only the
// glass buttons. Colours come from the phone kit tokens (--phone-glass*, KonvoControls.module.css).

import {CaretDownIcon, CaretLeftIcon, XIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/ui/phone_header/PhoneHeader.module.css';
import {clsx} from 'clsx';
import React from 'react';

type NativeButton = React.ButtonHTMLAttributes<HTMLButtonElement>;

export {styles as phoneHeaderStyles};

/** The bar: back, title and actions in one row, with a hairline under it unless `line` is false. */
export const PhoneHeaderBar = React.forwardRef<
	HTMLElement,
	React.HTMLAttributes<HTMLElement> & {line?: boolean; as?: 'div' | 'header'}
>(({line = true, as = 'div', className, ...rest}, ref) =>
	React.createElement(as, {ref, className: clsx(styles.bar, line && styles.barLine, className), ...rest}),
);
PhoneHeaderBar.displayName = 'PhoneHeaderBar';

/** A round 44px glass button on its own (close, ⋯, a lone action). */
export const PhoneGlassButton = React.forwardRef<HTMLButtonElement, NativeButton & {label: string}>(
	({label, className, type = 'button', children, ...rest}, ref) => (
		<button ref={ref} type={type} className={clsx(styles.glass, styles.round, className)} aria-label={label} {...rest}>
			{children}
		</button>
	),
);
PhoneGlassButton.displayName = 'PhoneGlassButton';

/**
 * The glass back pill. `kind` picks the glyph: back (‹), close (×) for pages that sit on top,
 * down (⌄) for sheets that drop away. `count` shows other unread items after the ‹, as WhatsApp's.
 */
export const PhoneBackButton = React.forwardRef<
	HTMLButtonElement,
	NativeButton & {label: string; kind?: 'back' | 'close' | 'down'; count?: number | null}
>(({label, kind = 'back', count, className, type = 'button', ...rest}, ref) => {
	const showCount = count != null && count > 0;
	return (
		<button
			ref={ref}
			type={type}
			className={clsx(styles.glass, styles.round, showCount && styles.backWithCount, className)}
			aria-label={label}
			data-swipe-back={kind === 'back' ? '' : undefined}
			{...rest}
		>
			{kind === 'close' ? (
				<XIcon size={22} />
			) : kind === 'down' ? (
				<CaretDownIcon size={24} />
			) : (
				<CaretLeftIcon size={24} />
			)}
			{showCount && <span className={styles.backCount}>{count > 99 ? '99+' : count}</span>}
		</button>
	);
});
PhoneBackButton.displayName = 'PhoneBackButton';

/** One glass capsule holding several icon buttons (search and ⋯, as on WhatsApp). */
export const PhoneGlassCapsule = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({className, ...rest}, ref) => <div ref={ref} className={clsx(styles.glass, styles.capsule, className)} {...rest} />,
);
PhoneGlassCapsule.displayName = 'PhoneGlassCapsule';

/** An icon button inside a PhoneGlassCapsule. */
export const PhoneCapsuleButton = React.forwardRef<HTMLButtonElement, NativeButton & {label: string; active?: boolean}>(
	({label, active, className, type = 'button', ...rest}, ref) => (
		<button
			ref={ref}
			type={type}
			className={clsx(styles.capsuleButton, active && styles.capsuleButtonActive, className)}
			aria-label={label}
			{...rest}
		/>
	),
);
PhoneCapsuleButton.displayName = 'PhoneCapsuleButton';

/** The ink button for the one thing to do (Send, New). */
export const PhoneInkButton = React.forwardRef<HTMLButtonElement, NativeButton & {label: string}>(
	({label, className, type = 'button', ...rest}, ref) => (
		<button ref={ref} type={type} className={clsx(styles.ink, className)} aria-label={label} {...rest} />
	),
);
PhoneInkButton.displayName = 'PhoneInkButton';

/**
 * Photo or icon, then the title over a subtitle. Pass `onClick` to make the whole block a button
 * (contact info, rename); otherwise it is plain text.
 */
export function PhoneHeaderTitle({
	leading,
	title,
	subtitle,
	subtitleClassName,
	onClick,
	label,
	className,
	titleId,
}: {
	leading?: React.ReactNode;
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	subtitleClassName?: string;
	onClick?: () => void;
	label?: string;
	className?: string;
	titleId?: string;
}) {
	const body = (
		<>
			{leading != null && <span className={styles.leading}>{leading}</span>}
			<span className={styles.titles}>
				<span className={styles.title} id={titleId}>
					{title}
				</span>
				{subtitle != null && subtitle !== '' && (
					<span className={clsx(styles.subtitle, subtitleClassName)}>{subtitle}</span>
				)}
			</span>
		</>
	);
	if (onClick) {
		return (
			<button
				type="button"
				className={clsx(styles.titleBlock, styles.titleButton, className)}
				onClick={onClick}
				aria-label={label}
			>
				{body}
			</button>
		);
	}
	return <div className={clsx(styles.titleBlock, className)}>{body}</div>;
}

/** A 40px round tile for an icon where a page has no photo (a list, a dashboard). */
export function PhoneHeaderIcon({children, className}: {children: React.ReactNode; className?: string}) {
	return <span className={clsx(styles.iconTile, className)}>{children}</span>;
}
